<template>
  <div class="flex flex-col gap-7">
    <div class="flex flex-row items-center gap-4 sm:gap-5">
      <div class="relative self-start flex-shrink-0">
        <span
          class="block rounded-full p-[3px]"
          :style="{ background: live ? '#E91916' : '#374151' }"
        >
          <LanAvatar :src="creator.picture_url" size="w-20 h-20 md:w-28 md:h-28 text-3xl border-4 border-gray-900" />
        </span>
        <span
          v-if="live"
          class="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[11px] font-bold text-white px-2 py-0.5 rounded"
          style="background:#E91916"
        >
          {{ $t('play.lan_party.live') }}
        </span>
      </div>
      <div class="min-w-0 flex flex-col gap-2">
        <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.refrag_creator') }}</p>
        <h2 class="text-text-primary font-semibold text-3xl md:text-4xl leading-none">{{ creator.name }}</h2>
        <p class="text-sm text-text-secondary flex flex-wrap items-center gap-x-2 gap-y-1">
          <a
            v-if="twitchName && channelUrl"
            :href="channelUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 hover:underline"
            style="color:#BF94FF"
          ><TwitchIcon class="w-4 h-4" />{{ twitchName }}</a>
          <TwitchViewers v-if="creator.twitch?.live" style="color:#BF94FF" :count="creator.twitch.viewer_count" />
          <span v-if="liveFor">{{ $t('play.lan_party.live_for', { time: liveFor }) }}</span>
        </p>
      </div>
    </div>

    <div v-if="lanParty" class="flex flex-col gap-3 border-t border-gray-800 pt-6">
      <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.in_the_lan_now', lanParty.member_count, { n: lanParty.member_count }) }}</p>
      <div v-if="lanParty.members.length" class="flex flex-wrap gap-1.5">
        <span
          v-for="member in members"
          :key="member.uuid"
          class="relative group"
          tabindex="0"
          :aria-label="member.uuid === me ? $t('play.lan_party.you') : member.username"
        >
          <LanAvatar
            :src="member.avatar"
            size="w-9 h-9 text-xs border-2"
            :class="member.uuid === me ? 'border-green-400' : 'border-gray-900'"
          />
          <span
            role="tooltip"
            class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 z-10 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] text-white shadow-lg opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          >{{ member.uuid === me ? $t('play.lan_party.you_named', { name: member.username }) : member.username }}</span>
        </span>
        <span
          v-if="lanParty.member_count > 14"
          class="w-9 h-9 rounded-full bg-gray-800 border-2 border-gray-900 text-[11px] font-semibold text-text-secondary flex items-center justify-center"
        >
          +{{ lanParty.member_count - 14 }}
        </span>
      </div>
      <p v-else class="text-sm text-text-secondary">{{ $t('play.lan_party.nobody_yet_viewer') }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import moment from 'moment';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import LanAvatar from './LanAvatar.vue';
import TwitchIcon from './TwitchIcon.vue';
import TwitchViewers from './TwitchViewers.vue';
import { safeHttpUrl, twitchLogin } from '../../utils/lan_party';

const auth = useAuth();
const props = defineProps({
  creator: { type: Object, required: true },
});

const lanParty = computed(() => props.creator.lan_party || null);
const me = computed(() => auth.user()?.uuid || null);
// You first, so you always find yourself in the strip.
const members = computed(() => {
  const all = lanParty.value?.members || [];
  const mine = all.filter((m) => m.uuid === me.value);
  return [...mine, ...all.filter((m) => m.uuid !== me.value)].slice(0, 14);
});
// LIVE is the Twitch stream, not the LAN party: a Creator can run one off stream.
const live = computed(() => !!props.creator.twitch?.live);
const channelUrl = computed(() => safeHttpUrl(props.creator.channel_url));
const twitchName = computed(() => twitchLogin(props.creator.channel_url));
const liveFor = computed(() => {
  const start = lanParty.value?.started_at;
  if (!start) return '';
  const minutes = Math.max(0, moment().diff(moment(start), 'minutes'));
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
});
</script>
